<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  :src="currentLogoImage"
                  :class="isCollapsedMini ? 'ml-1' : ''"
                  alt="logo"
                />
              </span>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :key="$store.getters['auth/userPermissions'].length"
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import {
  provide, computed, ref, watch,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import store from '@/store'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage, appLogoImageCollapsed } = $themeConfig.app

    const currentLogoImage = computed(() => (
      isVerticalMenuCollapsed.value && !isMouseHovered.value
        ? appLogoImageCollapsed
        : appLogoImage
    ))

    const isCollapsedMini = computed(() => isVerticalMenuCollapsed.value && !isMouseHovered.value)
    // 1. Initialize as a reactive ref
    const navMenuItemsReactive = ref([])

    // 2. Define the transformation logic
    const getFilteredMenu = () => {
      const permissions = store.getters['auth/userPermissions'] || []
      const isOwner = !!store.getters['auth/isOwner'] //

      const transform = items => items
        .map(item => {
          // 1. Owner Bypass
          if (isOwner) return item

          // 2. Recursive check for groups
          if (item.children) {
            const visibleChildren = transform(item.children)
            return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null
          }

          // 3. Permission Logic (resource first, then legacy permission key)
          const resourceKey = item.resource || item.permission

          // If no key exists (like Profile), it's a public item
          if (!resourceKey) return item

          // Check if the key exists in your 35 permissions
          return permissions.includes(resourceKey) ? item : null
        })
        .filter(item => item !== null)

      // Explicitly capture and return to clear the IDE "void" warning
      return transform(navMenuItems)
    }

    // 3. Watch for store changes and update the ref
    // This is the "trigger" that will fill your sidebar once permissions land
    watch(
      () => [
        store.getters['auth/isOwner'],
        ...(store.getters['auth/userPermissions'] || []),
      ],
      () => {
        navMenuItemsReactive.value = getFilteredMenu()
      },
      { immediate: true }, // Run immediately to catch existing data
    )

    // This log should now show the 6 items once the API returns
    return {
      navMenuItems: navMenuItemsReactive,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      currentLogoImage,
      isCollapsedMini,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
